import {
    RiAddCircleLine,
    RiArticleLine,
    RiBookmark3Line,
    RiBookmarkLine,
    RiCalendarScheduleLine,
    RiCloseCircleFill,
    RiDraggable, RiEyeLine, RiFileCopyLine,
    RiImageAddLine,
    RiMoreFill, RiPencilLine,
} from "@remixicon/react";
import classNames from "classnames";
import React, {
    ReactNode, useEffect, useMemo, useState,
} from "react";
import TimeAgo from "react-timeago";
import {
    Article as ApiArticle,
    Article,
    ArticleFeedback,
    ArticleSection, ArticleSectionType, ArticleTag,
    ArticleType,
    GetArticlesByTagDocument,
    Permission,
    PermissionAction,
    PermissionAssignment,
    useDeleteArticleMutation,
    useGetArticleTagsQuery, useSaveArticleMutation,
    useSetIndexArticleMutation,
} from "../../../__generated__/api";
import { cloneWithoutTypename } from "../../../api";
import { ReactComponent as EmptyArticleDark } from "../../../assets/img/empty-article-dark.svg";
import { ReactComponent as EmptyArticleLight } from "../../../assets/img/empty-article-light.svg";
import { useConfirm } from "../../../components/confirm/Confirm";
import useDeleteConfirm from "../../../components/confirm/Delete";
import { Property, useFormBuilder } from "../../../components/dirty-tracker/FormTracker";
import EditableControl from "../../../components/editable-text/EditableText";
import IconUploader from "../../../components/pickers/icon-uploader/IconUploader";
import { EVERYONE_GROUP_ID } from "../../../components/pickers/user-groups/UserGroups";
import Badge from "../../../components/theme/badge/Badge";
import Button from "../../../components/theme/button/Button";
import Dropdown from "../../../components/theme/dropdown/Dropdown";
import Icon from "../../../components/theme/icon/Icon";
import SortableList from "../../../components/theme/sortable/SortableList";
import Text from "../../../components/theme/text/Text";
import useRouting, { Paths, useParams } from "../../../contexts/Routing";
import { Themes, useTheme } from "../../../contexts/Theme";
import { LoggingHandler } from "../../../contexts/logging/LoggingContext";
import useCurrentSession from "../../../contexts/session";
import createId from "../../../create-id";
import { tryParseDate } from "../../../date";
import { useDebounce } from "../../../debouncer";
import useChangingCallback from "../../../use-changing-callback";
import { DEEP_COMPARER } from "../../administrator/sections/base";
import Styles from "./AnswerDetails.module.scss";
import { useScrollTop } from "./BackToTop";
import InsertMenu from "./InsertMenu";
import PublishButton, { PublishType } from "./Publish";
import UnpublishButton from "./Unpublish";
import ArticlePermissions from "./article-permissions";
import ArticleTagPicker from "./article-tags/ArticleTagPicker";
import Blocks from "./blocks";
import Feedback from "./feedback/Feedback";
import RelatedArticles from "./related-articles/RelatedArticles";
import useVersions from "./versions";
import ChangeTypeButton from "./ChangeType";

export function createLoadingArticle() {
    return {
        id: "123",
        type: ArticleType.Article,
        title: "Loading test article Name",
        body: {
            html: "<p>Hello</p>",
        },
        modifiedDate: new Date(),
        sections: new Array(Math.ceil(Math.random() * 5) + 1).fill(0).map(() => ({
            id: "123",
            type: ArticleSectionType.Html,
            text: {
                body: {
                    html: `<p>${"testwords ".repeat((Math.random() * 100) + 50)}</p>`,
                },
            },
        })),
        tags: [{
            id: "123",
            title: "Test",
            childIds: [],
        }, {
            id: "1234",
            title: "Loading",
            childIds: [],
        }],
        createdDate: new Date(),
        portal: {
            id: "123",
            name: "Test",
            guestPermissions: [],
            permissions: [],
            publicPermissions: [],
        },
        portalId: "123",
    } as Article;
}

export default function ArticleView({
    isReadOnly: isArticleReadOnly, article, isLoading, isMinimalActions, actionButtons, onPublish,
    shouldShowHeader, scrollRef, canPreview, canPublishKnowledge, isNewArticle, articleType,
}: {
    isReadOnly?: boolean;
    article: ApiArticle;
    isLoading?: boolean;
    isMinimalActions?: boolean;
    actionButtons?: ReactNode;
    onPublish?: (type: PublishType) => void;
    shouldShowHeader?: boolean;
    scrollRef?: React.RefObject<HTMLDivElement>;
    canPreview?: boolean;
    canPublishKnowledge?: boolean;
    isNewArticle?: boolean;
    articleType?: ArticleType;
}) {
    const { id: articleId } = useParams(Paths.ANSWERS_GET);
    const [isReadOnly, setIsReadOnly] = useState(isArticleReadOnly);
    const [saveAnswer] = useSaveArticleMutation();
    const [deleteAnswer] = useDeleteArticleMutation();
    const { permissions } = useCurrentSession();

    const { historyPush, getSearchParams } = useRouting();
    const theme = useTheme();

    const { tagId } = getSearchParams(Paths.ANSWERS_NEW);
    const [isDragging, setIsDragging] = useState(false);

    const { data: tagsData } = useGetArticleTagsQuery();

    const [id, setId] = useState(article?.id ?? articleId);

    const isNew = isNewArticle ?? (!article && id === "new");

    useEffect(() => {
        setIsReadOnly(isArticleReadOnly || article?.isEditableVersion === false);
    }, [isLoading, isArticleReadOnly, article]);

    const { tracker, properties } = useFormBuilder(
        builder => builder
            .property("title", () => article?.title ?? "")
            .property("iconUrl", () => article?.iconUrl ?? "")
            .object("sections", () => article?.sections ?? [], DEEP_COMPARER)
            .object("permissions", () => article?.permissions ?? [], DEEP_COMPARER)
            .array("tags", () => article?.tags ?? tagsData?.articleTags.filter(tag => tag.id === tagId) ?? []),
        [article?.id, article?.versionId, isLoading, tagsData, tagId],
    );

    const saveAnswerHandler = async ({ shouldForceSave } = { shouldForceSave: false }) => {
        if (!shouldForceSave && ((!isNew && !tracker.isDirty)
            || (isNew && properties.title.value === "" && properties.sections.value.isEmpty))) {
            return undefined;
        }
        const allTags = [...properties.tags.cleanValue, ...properties.tags.value].toIdList("id").dedupe() as string[];
        const isNewTitle = isNew || properties.title.isDirty || properties.tags.isDirty;
        const refetchQueries = isNewTitle
            ? allTags.map(currentId => ({
                query: GetArticlesByTagDocument,
                variables: { tagId: currentId },
            }))
            : undefined;
        const created = await saveAnswer({
            refetchQueries,
            variables: {
                id: isNew ? undefined : id,
                article: {
                    type: articleType,
                    iconUrl: properties.iconUrl.value as string,
                    sections: properties.sections.value.map(section => ({
                        id: section.id,
                        type: section.type,
                        text: {
                            body: section.text?.body?.html ?? "",
                        },
                        media: section.media ? cloneWithoutTypename(section.media) : undefined,
                        permissions: section.permissions?.map(action => ({
                            action: action.action as PermissionAction,
                            assigneeId: action.assigneeId as string,
                        })),
                    })),
                    body: "",
                    tagIds: properties.tags.value.toIdList("id"),
                    title: properties.title.value ?? "Untitled Article",
                    permissions: properties.permissions.value.map(cloneWithoutTypename),
                    fromVersionId: article?.versionId,
                },
            },
        });
        if (isNew && articleType !== ArticleType.Knowledge) {
            historyPush(Paths.ANSWERS_GET, {
                id: created.data?.saveArticle.id,
            });
            return created;
        }
        setId(created.data?.saveArticle.id);

        tracker.snapshot();
        return created;
    };
    const save = useDebounce(saveAnswerHandler, 1000, [properties]);

    tracker.useOnChange(() => {
        save();
    }, [article?.id]);

    const getVersionId = async () => {
        let currentArticle = article;
        if (!currentArticle) {
            currentArticle = (await saveAnswerHandler({ shouldForceSave: true }))?.data?.saveArticle as Article;
        }

        return currentArticle?.versionId;
    };

    const actions = useMemo(() => ({

        async addSections(sections: Partial<ArticleSection>[], index?: number) {
            const versionId = await getVersionId();
            const copy = [
                ...properties.sections.value,
            ];
            copy.splice(index ?? copy.length, 0, ...sections.map(section => ({
                id: `urn:fq:article-version-section:(${versionId},${createId()})`,
                type: section.type as ArticleSectionType,
                ...section,
            })));
            properties.sections.setValue(copy);
        },
        async replaceSection(sectionId: string, type: ArticleSectionType, newSection: Partial<ArticleSection>) {
            const fullNewSection = {
                ...newSection,
                type,
                id: `urn:fq:article-version-section:(${await getVersionId()},${createId()})`,
            };
            const copy = [...properties.sections.value];

            const index = copy.findIndex(section => section.id === sectionId);
            if (index < 0) {
                copy.push(fullNewSection);
            } else {
                copy.splice(index, 1, fullNewSection);
            }
            properties.sections.setValue(copy);
        },
        async cloneSection(sectionId: string) {
            const index = properties.sections.value.findIndex(section => section.id === sectionId);
            if (index < 0) {
                return;
            }
            const copy = [...properties.sections.value];
            const section = copy[index];
            copy.splice(index, 0, {
                ...section,
                id: `urn:fq:article-version-section:(${await getVersionId()},${createId()})`,
            });

            properties.sections.setValue(copy);
        },
        deleteSection(sectionId: string) {
            const index = properties.sections.value.findIndex(section => section.id === sectionId);
            if (index < 0) {
                return;
            }
            const copy = [...properties.sections.value];
            copy.splice(index, 1);
            properties.sections.setValue(copy);
        },
        getAllSections() {
            return properties.sections.value;
        },
    }), [properties, article]);

    const setIconUrl = useChangingCallback((url: string | undefined) => {
        properties.iconUrl.setValue(url ?? "");
    }, [properties]);

    const { element: deleteElement, setDeleting } = useDeleteConfirm<ApiArticle>(async () => {
        const allTags = [...properties.tags.cleanValue, ...properties.tags.value].toIdList("id").dedupe() as string[];
        await deleteAnswer({
            refetchQueries: allTags.map(currentId => ({
                query: GetArticlesByTagDocument,
                variables: { tagId: currentId },
            })),
            variables: {
                id: article?.id,
            },
        });
        historyPush(Paths.ANSWERS_LIST);
    }, item => item.title);

    const [setIndexArticle] = useSetIndexArticleMutation();
    const { element: defaultElement, setShouldShow: setShouldShowSetDefault } = useConfirm({
        title: "Set as Default",
        confirmText: "Set as Default",
        closeText: "Cancel",
        onConfirm: async () => {
            await setIndexArticle({
                variables: { id },
            });
        },
        body: (
            <p>
                By making this page the default, this will be the first page that customers will see when
                they first open your portal by going to
                {" "}
                <a href={`https://${window.location.hostname}/`} target="_blank" rel="noreferrer">
                    https://
                    {window.location.hostname}
                    /
                </a>
                .
            </p>
        ),
    });

    const lastModifiedDate = article?.draftModifiedDate ?? article?.modifiedDate ?? article?.createdDate;
    const lastModifiedUser = article?.draftCreator ?? article?.creator;

    const isPublished = article?.publishedDate !== undefined;

    const updatesLine = useMemo(() => {
        const publishDate = tryParseDate(article?.publishedDate);
        const isFuturePublish = (publishDate?.getTime() ?? 0) > Date.now();
        const RootElement = isFuturePublish ? "em" : "span";
        return {
            lastModified: (!isArticleReadOnly || article?.isEditableVersion) && lastModifiedDate ? (
                <>
                    Last Modified
                    {" "}
                    <TimeAgo
                        date={lastModifiedDate}
                        minPeriod={60}
                        title={lastModifiedDate?.toLocaleString()}
                    />
                    {" by "}
                    {lastModifiedUser?.name}
                </>
            ) : undefined,
            lastPublished: article?.publishedDate ? (
                <RootElement>
                    <Icon
                        icon={isFuturePublish ? <RiCalendarScheduleLine /> : <RiBookmark3Line />}
                        size="xsmall"
                        variant="secondary"
                    />
                    {" "}
                    {isFuturePublish ? `Scheduled to Publish` : "Published"}
                    {" "}
                    <TimeAgo
                        date={article?.publishedDate}
                        minPeriod={60}
                        title={publishDate?.toLocaleString()}
                    />
                    {isFuturePublish && publishDate && ` on ${publishDate.toLocaleString()}`}
                    {" by "}
                    {article.publishedBy?.name}
                </RootElement>
            ) : undefined,
        };
    }, [lastModifiedDate, article?.modifiedDate]);

    const { topRef, element: scrollTopTopElement } = useScrollTop(scrollRef);

    const [shouldShowMore, setShouldShowMore] = useState(false);

    const { button: versionButton, element: versionElement } = useVersions({
        id,
        onClick: () => setShouldShowMore(false),
        currentVersionId: article?.versionId,
        variant: article?.isEditableVersion === false ? "button" : "dropdown",
    });

    return (
        <article className={classNames(Styles["article-panel"], isLoading ? `${Styles["article-panel-loading"]} loading` : "")}>
            {deleteElement}
            {defaultElement}
            {versionElement}
            {shouldShowHeader ? (
                <div className={Styles["article-header"]} ref={topRef}>
                    <IconUploader
                        value={properties.iconUrl.value as string}
                        onChange={setIconUrl}
                        shouldStore
                        isReadOnly={isReadOnly}
                        shouldShowBorder={false}
                        size="md"
                        imageClassName={!isReadOnly ? Styles["icon-uploader-image"] : Styles["icon-uploader-image-readonly"]}
                        className={Styles["icon-uploader"]}
                        buttonsClassName={Styles["icon-uploader-buttons"]}
                        dropClassName={Styles["icon-uploader-drop"]}
                        placeholder={isReadOnly ? <RiArticleLine /> : <RiImageAddLine />}
                        isSquare
                    />
                    <div className={Styles["article-header-text"]}>
                        <Text as="h1" textVariant="title">

                            <EditableControl
                                isReadOnly={isReadOnly}
                                value={properties.title.value}
                                onChange={value => properties.title.setValue(value as string)}
                                inputClassName={Styles["article-title-input"]}
                                placeholderClassName={Styles["article-title-input-placeholder"]}
                                placeholder="Untitled"
                            />

                        </Text>
                        <Text textVariant="body-2-regular" colorVariant="body-secondary">
                            {updatesLine.lastPublished }
                            {updatesLine.lastPublished && updatesLine.lastModified ? " • " : ""}
                            {updatesLine.lastModified}

                        </Text>
                        <div className={Styles["article-header-tags"]}>
                            {properties.tags.value.map(tag => (
                                <Badge variant="gray" key={tag.id}>
                                    #
                                    {tag.title}
                                    {" "}
                                    {!isReadOnly && (
                                        <Button
                                            prefixIcon={<RiCloseCircleFill />}
                                            size="xsmall"
                                            variant="ghost-gray"
                                            aria-label="Close"
                                            onClick={() => {
                                                const copy = properties.tags.value.filter(item => item.id !== tag.id);
                                                properties.tags.setValue(copy);
                                            }}
                                        />
                                    )}
                                </Badge>
                            ))}
                            {!isReadOnly && (
                                <ArticleTagPicker tags={properties.tags as Property<Partial<ArticleTag>[]>} />
                            )}
                        </div>

                    </div>
                    <div className={Styles["article-header-actions"]}>
                        {!isArticleReadOnly && !isMinimalActions && article?.isEditableVersion === false && versionButton}
                        {!isArticleReadOnly && article?.isEditableVersion !== false && (
                            <>
                                {canPreview && (
                                    <Button
                                        onClick={() => setIsReadOnly(current => !current)}
                                        prefixIcon={isReadOnly ? <RiPencilLine /> : <RiEyeLine />}
                                        variant="outline-blue"
                                        size="small"
                                    >
                                        {isReadOnly ? "Edit" : "Preview"}
                                    </Button>
                                )}

                                <PublishButton
                                    article={article as ApiArticle}
                                    canPublish={!tracker.isDirty}
                                    onPublish={onPublish}
                                    canPublishKnowledge={canPublishKnowledge}
                                />

                                {actionButtons}

                                {!isMinimalActions && (
                                    <Dropdown placement="bottom-start" show={shouldShowMore}>
                                        <Dropdown.Toggle
                                            prefixIcon={
                                                <Icon.More />
                                            }
                                            variant="outline-gray"
                                            size="small"
                                            isClickable={!isLoading}
                                            onClick={() => setShouldShowMore(!shouldShowMore)}
                                        />
                                        <Dropdown.Menu>

                                            {permissions.has(Permission.Administrator) && (
                                                <Dropdown.Item
                                                    disabled={!isPublished}
                                                    onClick={() => {
                                                        setShouldShowSetDefault(true);
                                                        setShouldShowMore(!shouldShowMore);
                                                    }}
                                                    prefixIcon={<Icon icon={<RiBookmarkLine />} />}
                                                    title={isPublished
                                                        ? "Set this article as the default. This will make this"
                                                         + " the first page that appears when a customer opens your portal"
                                                        : "To make this article the default, first publish it"}
                                                >
                                                    Set Default
                                                </Dropdown.Item>
                                            )}
                                            {isPublished && (
                                                <UnpublishButton
                                                    article={article as ApiArticle}
                                                    onClick={() => setShouldShowMore(!shouldShowMore)}
                                                />
                                            )}

                                            <Dropdown.Item>
                                                <ArticlePermissions
                                                    toggleSize="small"
                                                    toggleVariant="none"
                                                    title="Article Permissions"
                                                    text="Set the permissions for this article"
                                                    shouldColor={false}
                                                    onChange={value => {
                                                        properties.permissions.setValue(value);
                                                        setShouldShowMore(!shouldShowMore);
                                                    }}
                                                    value={properties.permissions.value}
                                                />
                                            </Dropdown.Item>

                                            {versionButton}
                                            <ChangeTypeButton
                                                article={article as ApiArticle}
                                                onClick={() => setShouldShowMore(false)}
                                            />
                                            <Dropdown.Divider />
                                            <Dropdown.Item
                                                onClick={() => {
                                                    setDeleting(article as ApiArticle);
                                                    setShouldShowMore(!shouldShowMore);
                                                }}
                                                prefixIcon={<Icon.Delete variant="danger" />}
                                            >
                                                <Text colorVariant="danger" textVariant="body-2-medium">
                                                    Delete
                                                </Text>
                                            </Dropdown.Item>

                                        </Dropdown.Menu>
                                    </Dropdown>
                                )}
                            </>
                        )}
                    </div>
                </div>
            ) : (
                <div ref={topRef} />
            )}
            <div className={Styles["article-body"]}>
                {scrollTopTopElement}

                {isReadOnly ? (
                    <>
                        {properties.sections.value.map(section => {
                            const Block = Blocks[section.type];
                            return Block ? <Block className="" key={section.id} section={section} isReadOnly /> : undefined;
                        })}
                        {!properties.sections.value.some(row => row.text?.body?.html || row.media?.url || row.search) && (
                            <div className={Styles["empty-article"]}>
                                <Text textVariant="body-1-regular" colorVariant="body-secondary">
                                    <em>
                                        Article is empty
                                    </em>
                                </Text>
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        <SortableList
                            items={properties.sections.value}
                            id="sections"
                            className={Styles["article-body-sections"]}
                            onReorder={items => properties.sections.setValue(items)}
                            onDragStart={() => setIsDragging(true)}
                            onDragEnd={() => setIsDragging(false)}
                        >
                            {properties.sections.value.map((section, index) => {
                                const Block = Blocks[section.type];
                                const isPublic = section.permissions?.filter(row => row.action === PermissionAction.Read
                                    && row.assigneeId !== EVERYONE_GROUP_ID).isEmpty ?? true;

                                const isSectionReadOnly = section.permissions?.isNotEmpty
                                    && section.currentUserPermission !== PermissionAction.Modify;
                                if (isSectionReadOnly) {
                                    return <Block className="" key={section.id} section={section} isReadOnly />;
                                }

                                return (
                                    <SortableList.Item
                                        isEnabled={!isReadOnly}
                                        nodeType="section"
                                        item={section}
                                        className={classNames(
                                            Styles["article-section-container"],
                                        )}
                                        key={section.id}
                                        shouldDragWholeElement={false}
                                    >
                                        <div className={classNames(
                                            Styles["article-section"],
                                            Styles[`article-section-${isPublic ? "public" : "private"}`],
                                        )}
                                        >
                                            <SortableList.Listener className={Styles["widget-draggable"]}>
                                                <RiDraggable />
                                            </SortableList.Listener>
                                            <div className={classNames(
                                                Styles["article-editor-container"],
                                                Styles[`article-editor-container-${section.type.toLowerCase()}`],
                                            )}
                                            >
                                                <Block
                                                    className={Styles[`article-section-block-${
                                                        isPublic ? "public" : "private"
                                                    }`]}
                                                    isReadOnly={false}
                                                    section={section}
                                                    onChange={newSection => {
                                                        properties.sections.setValue(
                                                            properties.sections.value.map(item => (item.id === section.id
                                                                ? newSection
                                                                : item
                                                            )),
                                                        );
                                                    }}
                                                />
                                                <div className={Styles["article-block-permissions"]}>
                                                    <ArticlePermissions
                                                        value={section.permissions as PermissionAssignment[] ?? []}
                                                        onChange={articlePermissions => {
                                                            properties.sections.setValue(
                                                                properties.sections.value.map(item => (item.id === section.id
                                                                    ? {
                                                                        ...section,
                                                                        permissions: articlePermissions,
                                                                    }
                                                                    : item
                                                                )),
                                                            );
                                                        }}
                                                    />
                                                </div>
                                                {!isDragging && index + 1 < properties.sections.value.length && (
                                                    <div className={classNames(Styles["article-add-block-container"])}>
                                                        <div className={classNames(Styles["article-add-block"])}>
                                                            <Dropdown placement="bottom-start">
                                                                <Dropdown.Toggle
                                                                    prefixIcon={<RiAddCircleLine />}
                                                                    variant="ghost-blue"
                                                                    size="small"
                                                                >
                                                                    Add New Section
                                                                </Dropdown.Toggle>
                                                                <InsertMenu
                                                                    index={index + 1}
                                                                    properties={properties}
                                                                    actions={actions}
                                                                    isPageEmpty={false}
                                                                />
                                                            </Dropdown>
                                                        </div>
                                                    </div>
                                                )}

                                            </div>
                                            <div className={Styles["article-editor-actions"]}>
                                                <Button
                                                    prefixIcon={
                                                        <Icon icon={<RiFileCopyLine />} variant="default" />
                                                    }
                                                    variant="ghost-gray"
                                                    size="small"
                                                    metricPath={[LoggingHandler.Metrics.ARTICLES, "sections", "clone"]}
                                                    onClick={() => actions.cloneSection(section.id)}
                                                />
                                                <Dropdown placement="bottom-start">
                                                    <Dropdown.Toggle
                                                        prefixIcon={
                                                            <Icon icon={<RiMoreFill />} variant="default" />
                                                        }
                                                        variant="ghost-blue"
                                                        size="small"
                                                    />
                                                    <Dropdown.Menu>
                                                        <Dropdown.Item
                                                            metricPath={[LoggingHandler.Metrics.ARTICLES, "sections", "delete"]}
                                                            onClick={() => {
                                                                actions.deleteSection(section.id);
                                                            }}
                                                            prefixIcon={<Icon.Delete />}
                                                        >
                                                            Delete
                                                        </Dropdown.Item>
                                                    </Dropdown.Menu>
                                                </Dropdown>

                                            </div>
                                        </div>

                                    </SortableList.Item>
                                );
                            })}
                        </SortableList>

                        {!isLoading && (
                            properties.sections.value.isEmpty ? (
                                <div className={Styles["empty-article"]}>
                                    {theme.getTheme() === Themes.LIGHT ? <EmptyArticleLight /> : <EmptyArticleDark />}
                                    <h4>
                                        Get Started
                                    </h4>
                                    <p>Start by adding blocks to build your content.</p>
                                    <Dropdown placement="right-end">
                                        <Dropdown.Toggle prefixIcon={<Icon.Add />}>
                                            Add Block
                                        </Dropdown.Toggle>
                                        <InsertMenu properties={properties} actions={actions} isPageEmpty />
                                    </Dropdown>
                                </div>

                            ) : (
                                <Dropdown placement="top-end" className={Styles["article-add-block-large"]}>
                                    <Dropdown.Toggle
                                        prefixIcon={<RiAddCircleLine />}
                                        size="small"
                                        variant="none"
                                    >
                                        Add New Section
                                    </Dropdown.Toggle>
                                    <InsertMenu properties={properties} actions={actions} isPageEmpty={false} />
                                </Dropdown>
                            )
                        )}
                    </>
                )}
            </div>
            {!isMinimalActions && article?.isEditableVersion !== false && !isLoading && isReadOnly && !isNew && (
                <div className={Styles["article-footer"]}>
                    <hr className={Styles["article-footer-border"]} />
                    {article?.relatedArticles?.isNotEmpty && (
                        <div>
                            <Text textVariant="subtitle-2" colorVariant="text-title">Related Articles</Text>
                            <RelatedArticles articles={article?.relatedArticles} />
                        </div>
                    )}
                    <div>
                        <Text textVariant="subtitle-2" colorVariant="text-title">Help & Support</Text>
                        <div className={Styles["article-footer-section"]}>
                            <Feedback id={id} feedback={article?.feedback as ArticleFeedback} />
                        </div>
                    </div>
                </div>
            )}
        </article>
    );
}

ArticleView.defaultProps = {
    isReadOnly: false,
    isLoading: false,
    isMinimalActions: false,
    actionButtons: undefined,
    onPublish: undefined,
    shouldShowHeader: true,
    scrollRef: undefined,
    canPreview: true,
    canPublishKnowledge: false,
    isNewArticle: undefined,
    articleType: undefined,
};
